/**
 * MEVIA - Company Subscription View
 * Shows the company admin their current subscription, limits, and usage
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  CreditCard, 
  Check, 
  X, 
  Users, 
  FileText, 
  Calendar,
  AlertTriangle,
  Crown,
  Shield,
  Zap,
  ArrowLeft,
  Clock,
  BarChart3,
  Lock,
  Unlock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

const CompanySubscriptionView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [error, setError] = useState(null);

  const t = {
    es: {
      title: 'Mi Suscripción',
      subtitle: 'Información de tu plan actual y límites',
      back: 'Volver al Dashboard',
      currentPlan: 'Plan Actual',
      noPlan: 'Sin Plan Activo',
      noPlanDesc: 'Tu empresa no tiene una suscripción activa. Contacta al administrador.',
      trial: 'Período de Prueba',
      trialEnds: 'Finaliza el',
      expired: 'Expirado',
      active: 'Activo',
      usage: 'Uso y Límites',
      employees: 'Empleados',
      evaluations: 'Evaluaciones',
      unlimited: 'Ilimitado',
      of: 'de',
      used: 'usadas',
      modules: 'Módulos Incluidos',
      features: 'Características',
      enabled: 'Habilitado',
      disabled: 'No incluido',
      validUntil: 'Válido hasta',
      noExpiration: 'Sin fecha de expiración',
      contactAdmin: 'Contactar Administrador',
      upgradeHint: 'Para actualizar tu plan, contacta al equipo de MEVIA',
      moduleLabels: {
        culture: 'Cultura Organizacional',
        personality: 'Personalidad (Eneagrama)',
        integrated_report: 'Reporte Integrado',
        hr_indicators: 'Indicadores de RH',
        alerts: 'Alertas de Riesgo',
        action_plans: 'Planes de Acción',
        evaluation_periods: 'Periodos de Evaluación',
        profile_evaluation: 'Evaluación por Perfiles',
        corporate_identity: 'Identidad Corporativa',
        pdf_export: 'Exportar a PDF',
        candidate_evaluation: 'Evaluación de Candidatos',
        nom035: 'NOM-035',
        orgchart: 'Organigrama',
        dashboard_central: 'Dashboard Central',
        evolution_impact: 'ROI e Impacto Evolutivo',
        pulse_surveys: 'Encuestas de Pulso',
        strategic_plans: 'Planes Estratégicos',
        evaluation_cycles: 'Ciclos de Evaluación',
        company_profile: 'Perfil de Empresa',
        compatibility_4d: 'Compatibilidad 4D'
      },
      featureLabels: {
        basic_reports: 'Reportes Básicos',
        advanced_reports: 'Reportes Avanzados',
        api_access: 'Acceso API',
        priority_support: 'Soporte Prioritario'
      }
    },
    en: {
      title: 'My Subscription',
      subtitle: 'Your current plan information and limits',
      back: 'Back to Dashboard',
      currentPlan: 'Current Plan',
      noPlan: 'No Active Plan',
      noPlanDesc: 'Your company does not have an active subscription. Contact the administrator.',
      trial: 'Trial Period',
      trialEnds: 'Ends on',
      expired: 'Expired',
      active: 'Active',
      usage: 'Usage & Limits',
      employees: 'Employees',
      evaluations: 'Evaluations',
      unlimited: 'Unlimited',
      of: 'of',
      used: 'used',
      modules: 'Included Modules',
      features: 'Features',
      enabled: 'Enabled',
      disabled: 'Not included',
      validUntil: 'Valid until',
      noExpiration: 'No expiration date',
      contactAdmin: 'Contact Administrator',
      upgradeHint: 'To upgrade your plan, contact the MEVIA team',
      moduleLabels: {
        culture: 'Organizational Culture',
        personality: 'Personality (Enneagram)',
        integrated_report: 'Integrated Report',
        hr_indicators: 'HR Indicators',
        alerts: 'Risk Alerts',
        action_plans: 'Action Plans',
        evaluation_periods: 'Evaluation Periods',
        profile_evaluation: 'Profile Evaluation',
        corporate_identity: 'Corporate Identity',
        pdf_export: 'PDF Export',
        candidate_evaluation: 'Candidate Evaluation',
        nom035: 'NOM-035',
        orgchart: 'Org Chart',
        dashboard_central: 'Central Dashboard',
        evolution_impact: 'ROI & Evolution Impact',
        pulse_surveys: 'Pulse Surveys',
        strategic_plans: 'Strategic Plans',
        evaluation_cycles: 'Evaluation Cycles',
        company_profile: 'Company Profile',
        compatibility_4d: '4D Compatibility'
      },
      featureLabels: {
        basic_reports: 'Basic Reports',
        advanced_reports: 'Advanced Reports',
        api_access: 'API Access',
        priority_support: 'Priority Support'
      }
    }
  }[language];

  useEffect(() => {
    loadSubscriptionData();
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user?.empresa_id) {
      setLoading(false);
      setError('No company associated');
      return;
    }

    try {
      const [subRes, permRes] = await Promise.all([
        axios.get(`/subscriptions/company/${user.empresa_id}`),
        axios.get(`/subscriptions/permissions/${user.empresa_id}`)
      ]);
      
      setSubscription(subRes.data.data);
      setPermissions(permRes.data.data);
    } catch (error) {
      logger.error('Error loading subscription:', error);
      setError('Error loading subscription data');
    } finally {
      setLoading(false);
    }
  };

  const getPlanIcon = (planName) => {
    const icons = {
      'Trial': Clock,
      'Starter': Shield,
      'Growth': Zap,
      'Enterprise': Crown
    };
    return icons[planName] || CreditCard;
  };

  const getPlanColor = (planName) => {
    const colors = {
      'Trial': 'bg-gray-100 text-gray-700 border-gray-300',
      'Starter': 'bg-blue-100 text-blue-700 border-blue-300',
      'Growth': 'bg-purple-100 text-purple-700 border-purple-300',
      'Enterprise': 'bg-amber-100 text-amber-700 border-amber-300'
    };
    return colors[planName] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateProgress = (used, max) => {
    if (max === -1) return 0; // Unlimited
    return Math.min((used / max) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  const PlanIcon = permissions?.plan_name ? getPlanIcon(permissions.plan_name) : CreditCard;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderBottomColor: '#1A878D', borderBottomWidth: '2px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t.back}
              </Button>
            </div>
            <img src="/images/mevia-logo-horizontal.png" alt="MEVIA" className="h-8" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="h-8 w-8 text-teal-600" />
            {t.title}
          </h1>
          <p className="mt-2 text-gray-600">{t.subtitle}</p>
        </div>

        {!permissions?.has_active_subscription ? (
          /* No Active Subscription */
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t.noPlan}</h3>
              <p className="text-gray-600 mb-6">{t.noPlanDesc}</p>
              <Button variant="outline" onClick={() => window.location.href = 'mailto:hola@mevia.mx'}>
                {t.contactAdmin}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Current Plan Card */}
            <Card className={`border-2 ${getPlanColor(permissions.plan_name)}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${getPlanColor(permissions.plan_name)}`}>
                      <PlanIcon className="h-8 w-8" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">{permissions.plan_name}</CardTitle>
                      <CardDescription>{t.currentPlan}</CardDescription>
                    </div>
                  </div>
                  <div className="text-right">
                    {permissions.is_trial ? (
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                        <Clock className="h-3 w-3 mr-1" />
                        {t.trial}
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">
                        <Check className="h-3 w-3 mr-1" />
                        {t.active}
                      </Badge>
                    )}
                    {permissions.end_date && (
                      <p className="text-sm text-gray-500 mt-2">
                        {t.validUntil}: {formatDate(permissions.end_date)}
                      </p>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Usage & Limits */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-teal-600" />
                  {t.usage}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Employees */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Users className="h-4 w-4 text-gray-400" />
                      {t.employees}
                    </span>
                    <span className="text-sm text-gray-600">
                      {permissions.limits.max_employees === -1 
                        ? t.unlimited 
                        : `${permissions.limits.max_employees}`}
                    </span>
                  </div>
                  {permissions.limits.max_employees !== -1 && (
                    <Progress 
                      value={0} 
                      className="h-2"
                    />
                  )}
                </div>

                {/* Evaluations */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <FileText className="h-4 w-4 text-gray-400" />
                      {t.evaluations}
                    </span>
                    <span className="text-sm text-gray-600">
                      {permissions.limits.evaluations_used} {t.used}
                      {permissions.limits.max_evaluations !== -1 && (
                        <> {t.of} {permissions.limits.max_evaluations}</>
                      )}
                      {permissions.limits.max_evaluations === -1 && (
                        <> ({t.unlimited})</>
                      )}
                    </span>
                  </div>
                  {permissions.limits.max_evaluations !== -1 && (
                    <Progress 
                      value={calculateProgress(permissions.limits.evaluations_used, permissions.limits.max_evaluations)} 
                      className="h-2"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Modules - D10: 3 states */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-teal-600" />
                  {t.modules}
                </CardTitle>
                <CardDescription className="text-xs">
                  {language === 'es' 
                    ? 'Verde = Activo | Gris = Disponible (desactivado por admin) | Rojo = No incluido en plan'
                    : 'Green = Active | Gray = Available (disabled by admin) | Red = Not in plan'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(t.moduleLabels).map(([key, label]) => {
                    const planAllows = permissions.plan_ceiling?.[key] ?? false;
                    const isEnabled = permissions.permissions[key];
                    const adminDisabled = planAllows && !isEnabled;
                    
                    return (
                      <div 
                        key={key}
                        data-testid={`module-${key}`}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isEnabled
                            ? 'bg-green-50 border-green-200'
                            : adminDisabled
                              ? 'bg-slate-50 border-slate-200'
                              : 'bg-red-50 border-red-100'
                        }`}
                      >
                        <span className={`text-sm ${
                          isEnabled ? 'text-gray-900' 
                          : adminDisabled ? 'text-gray-500' 
                          : 'text-red-300'
                        }`}>
                          {label}
                        </span>
                        {isEnabled ? (
                          <Unlock className="h-4 w-4 text-green-600" />
                        ) : adminDisabled ? (
                          <span className="text-xs text-gray-400">{language === 'es' ? 'OFF' : 'OFF'}</span>
                        ) : (
                          <Lock className="h-4 w-4 text-red-400" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-teal-600" />
                  {t.features}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(t.featureLabels).map(([key, label]) => {
                    const isEnabled = permissions.permissions[key];
                    return (
                      <div 
                        key={key}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isEnabled 
                            ? 'bg-blue-50 border-blue-200' 
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <span className={`text-sm ${isEnabled ? 'text-gray-900' : 'text-gray-400'}`}>
                          {label}
                        </span>
                        {isEnabled ? (
                          <Check className="h-4 w-4 text-blue-600" />
                        ) : (
                          <X className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Upgrade Hint */}
            <div className="text-center text-sm text-gray-500 py-4">
              <p>{t.upgradeHint}</p>
              <a href="mailto:hola@mevia.mx" className="text-teal-600 hover:underline">
                hola@mevia.mx
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompanySubscriptionView;
